<template>
  <b-modal v-model="showModal" title="Importar usuarios por CSV" size="lg" hide-footer @hidden="onClose">
    <div>
      <b-form @submit.prevent="importarUsuarios">
        <b-form-group :label="isGestorEscolar ? 'Dominio (bloqueado)' : 'Dominio (opcional)'" label-for="dominio-select">
          <v-select
            id="dominio-select"
            v-model="dominioSeleccionado"
            :options="dominiosOptions"
            :disabled="dominiosLoading || dominioGestorBloqueado"
            :placeholder="isGestorEscolar ? 'Solo puedes dar de alta usuarios de tu dominio' : 'Selecciona un dominio (opcional)'"
            label="text"
          >
            <template v-slot:no-options="{ search, searching }">
              <template v-if="searching">
                No se encontraron resultados para <em>{{ search }}</em>.
              </template>
              <em v-else style="opacity: 0.5">Escribe para buscar.</em>
            </template>
          </v-select>
          <small v-if="isGestorEscolar" class="text-muted">
            <b-icon icon="info-circle" class="mr-1"></b-icon>
            Como Coordinador, solo puedes importar usuarios de tu dominio ({{ dominioSeleccionado ? dominioSeleccionado.value : '' }})
          </small>
        </b-form-group>

        <b-form-group label="Grupos (opcional)" label-for="grupos-select">
          <v-select
            id="grupos-select"
            v-model="gruposSeleccionados"
            :options="gruposOptions"
            :reduce="grupo => grupo.value"
            label="text"
            multiple
            placeholder="Selecciona grupos (opcional)"
          >
            <template v-slot:no-options="{ search, searching }">
              <template v-if="searching">
                No se encontraron resultados para <em>{{ search }}</em>.
              </template>
              <em v-else style="opacity: 0.5">Escribe para buscar.</em>
            </template>
          </v-select>
        </b-form-group>

        <!-- Sección de Mapeo de Columnas -->
        <b-card class="mb-3" border-variant="primary">
          <template #header>
            <h6 class="mb-0">
              <b-icon icon="table" class="mr-2"></b-icon>
              Mapeo de Columnas del CSV
            </h6>
          </template>
          <p class="text-muted small mb-3">
            <b-icon icon="info-circle" class="mr-1"></b-icon>
            Selecciona la columna (letra) del CSV donde se encuentra cada campo
          </p>
          <b-row>
            <b-col cols="12" md="6" v-for="campo in camposCsv" :key="campo.key">
              <b-form-group 
                :label="campo.key === 'rol_plataforma' && isGestorEscolar ? 'Rol Plataforma (bloqueado - solo Docente)' : campo.label" 
                :label-for="`col-${campo.key}`"
                class="mb-3"
              >
                <b-form-select
                  :id="`col-${campo.key}`"
                  v-model="columnMapping[campo.key]"
                  :options="columnasAlfabeticas"
                  :state="columnMapping[campo.key] ? true : null"
                  :disabled="campo.key === 'rol_plataforma' && isGestorEscolar"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Selecciona columna --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <small v-if="campo.key === 'rol_plataforma' && isGestorEscolar" class="text-muted">
                  <b-icon icon="info-circle" class="mr-1"></b-icon>
                  Como Coordinador, solo puedes dar de alta usuarios con rol "Docente" (estudiante en BD). No es necesario especificar esta columna.
                </small>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-form-group label="Archivo CSV" label-for="archivo-csv">
          <b-form-file
            id="archivo-csv"
            v-model="archivoCsv"
            accept=".csv"
            :state="archivoCsv ? true : null"
            placeholder="Selecciona archivo CSV..."
            browse-text="Buscar"
            required
            @change="onArchivoChange"
          />
        </b-form-group>

        <!-- Checkbox para indicar si el CSV tiene encabezados -->
        <b-form-group>
          <b-form-checkbox
            id="csv-has-header"
            v-model="csvHasHeader"
            name="csv-has-header"
          >
            <span class="ml-1">
              <b-icon icon="file-earmark-text" class="mr-1"></b-icon>
              El archivo CSV tiene encabezados en la primera fila
            </span>
          </b-form-checkbox>
          <small class="text-muted ml-4">
            Marca esta opción si la primera fila del CSV contiene nombres de columnas (Email, Nombre, etc.)
          </small>
        </b-form-group>

        <!-- Botón para previsualizar -->
        <div class="d-flex justify-content-end mb-3" v-if="archivoCsv && !previsualizacionDatos">
          <b-button 
            variant="info" 
            @click="previsualizarDatos" 
            :disabled="cargandoPreview || !columnMapping.email"
          >
            <b-spinner small v-if="cargandoPreview" class="mr-1" />
            <b-icon icon="eye" class="mr-1" v-else></b-icon>
            Previsualizar datos
          </b-button>
        </div>

        <!-- Sección de Previsualización -->
        <b-card v-if="previsualizacionDatos" class="mb-3" border-variant="success">
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                <b-icon icon="eye-fill" class="mr-2"></b-icon>
                Vista Previa de Datos
              </h6>
              <b-button size="sm" variant="outline-secondary" @click="limpiarPreview">
                <b-icon icon="x"></b-icon>
              </b-button>
            </div>
          </template>
          <p class="text-muted small mb-2">
            <b-icon icon="info-circle" class="mr-1"></b-icon>
            Mostrando {{ previsualizacionDatos.length }} de {{ totalRegistrosPreview }} registro(s)
          </p>

          <!-- Estadísticas de Roles -->
          <div v-if="estadisticasRoles" class="mb-3">
            <b-alert show variant="info" class="mb-0">
              <strong><b-icon icon="people-fill" class="mr-1"></b-icon>Desglose por roles:</strong>
              <ul class="mb-0 mt-2">
                <li v-for="(cantidad, rol) in estadisticasRoles" :key="rol">
                  <strong>{{ rol }}:</strong> {{ cantidad }} usuario(s)
                </li>
                <li v-if="estadisticasRoles.sin_rol" class="text-warning">
                  <strong>Sin rol especificado:</strong> {{ estadisticasRoles.sin_rol }} usuario(s)
                  <br><small><b-icon icon="arrow-right" class="mr-1"></b-icon>Se agregarán con rol <strong>"estudiante"</strong> por defecto</small>
                </li>
              </ul>
            </b-alert>
          </div>

          <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
            <b-table
              striped
              hover
              small
              :items="previsualizacionDatos"
              :fields="camposPreview"
              class="mb-0"
            >
              <template #cell(index)="data">
                {{ data.index + 1 }}
              </template>
              <template #cell(email)="data">
                <span :class="{ 'text-danger': !data.value }">
                  {{ data.value || '(vacío)' }}
                </span>
              </template>
              <template #cell(nombre)="data">
                <span :class="{ 'text-muted': !data.value }">
                  {{ data.value || '(no especificado)' }}
                </span>
              </template>
              <template #cell(rol_plataforma)="data">
                <span :class="{ 'text-muted': !data.value }">
                  {{ data.value || '(no especificado)' }}
                </span>
              </template>
            </b-table>
          </div>
          
          <!-- Advertencias encontradas -->
          <div v-if="advertencias && (advertencias.emails_invalidos?.total > 0 || advertencias.dominios_no_encontrados?.total > 0 || advertencias.otras?.length > 0)" class="mt-2">
            <b-alert show variant="warning" class="mb-4">
              <div class="w-100">
                <div class="mb-3">
                  <strong>Advertencias encontradas:</strong>
                </div>

                <!-- Emails Inválidos -->
                <div v-if="advertencias.emails_invalidos && advertencias.emails_invalidos.total > 0" class="mb-4">
                  <strong>Emails Inválidos:</strong>
                  <ul class="mt-2 mb-0">
                    <li v-for="(item, idx) in advertencias.emails_invalidos.lista" :key="'email-' + idx">
                      Fila {{ item.fila }}: Formato de email inválido ({{ item.email }})
                    </li>
                  </ul>
                </div>
                
                <!-- Dominios No Encontrados -->
                <div v-if="advertencias.dominios_no_encontrados && advertencias.dominios_no_encontrados.total > 0" class="mb-4">
                  <strong>Dominios No Encontrados:</strong>
                  <ul class="mt-2 mb-2">
                    <li>
                      Se encontraron {{ advertencias.dominios_no_encontrados.total }} dominio(s) que no existen en el sistema y serán creados automáticamente
                    </li>
                  </ul>
                  <div class="mt-2">
                    <strong class="d-block mb-2">Estos dominios serán creados automáticamente:</strong>
                    <div style="max-height: 200px; overflow-y: auto;">
                      <b-list-group>
                        <b-list-group-item 
                          v-for="(dominio, idx) in advertencias.dominios_no_encontrados.lista" 
                          :key="'dom-detail-' + idx"
                          class="d-flex justify-content-between align-items-center py-2"
                        >
                          <div>
                            <span>{{ dominio.dominio }}</span>
                          </div>
                          <div>
                            <small class="text-muted">
                              <b-icon icon="people-fill" class="mr-1"></b-icon>
                              {{ dominio.cantidad_usuarios }} usuario(s)
                            </small>
                          </div>
                        </b-list-group-item>
                      </b-list-group>
                    </div>
                  </div>
                </div>
                
                <!-- Otras Advertencias -->
                <div v-if="advertencias.otras && advertencias.otras.length > 0" class="mb-0">
                  <strong>OTRAS:</strong>
                  <ul class="mt-2 mb-0">
                    <li v-for="(advertencia, idx) in advertencias.otras" :key="'otra-' + idx">
                      {{ advertencia }}
                    </li>
                  </ul>
                </div>
              </div>
            </b-alert>
          </div> 
        </b-card>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" @click="cerrarModal">Cancelar</b-button>
          <b-button variant="primary" @click="mostrarConfirmacion" class="ml-2" :disabled="importando || !previsualizacionDatos">
            <b-spinner small v-if="importando" class="mr-1" />
            Importar
          </b-button>
        </div>
      </b-form>
    </div>

    <!-- Modal de Confirmación -->
    <b-modal v-model="showConfirmModal" title="Confirmar Importación" centered hide-footer size="lg">
      <div>
        <p class="mb-3">
          <b-icon icon="question-circle-fill" variant="warning" class="mr-2"></b-icon>
          ¿Estás seguro de que deseas importar <strong>{{ totalRegistrosPreview }}</strong> usuario(s)?
        </p>

        <!-- Advertencias Clasificadas -->
        <div v-if="advertencias">
          <b-alert v-if="advertencias.emails_invalidos && advertencias.emails_invalidos.total > 0" show variant="danger" class="mb-2">
            <strong>
              <b-icon icon="envelope-x-fill" class="mr-1"></b-icon>
              Se encontraron {{ advertencias.emails_invalidos.total }} email(s) con formato inválido
            </strong>
            <div class="mt-1">
              <small class="text-muted">Estas filas serán omitidas durante la importación</small>
            </div>
          </b-alert>

          <b-alert v-if="advertencias.dominios_no_encontrados && advertencias.dominios_no_encontrados.total > 0" show variant="warning" class="mb-2">
            <strong>
              <b-icon icon="building-add" class="mr-1"></b-icon>
              Se crearán {{ advertencias.dominios_no_encontrados.total }} nuevo(s) dominio(s)
            </strong>
            <ul class="mb-0 mt-2">
              <li v-for="(dominio, idx) in advertencias.dominios_no_encontrados.lista" :key="'dom-modal-' + idx">
                <small><strong>{{ dominio.dominio }}</strong> ({{ dominio.cantidad_usuarios }} usuario(s))</small>
              </li>
            </ul>
          </b-alert>

          <b-alert v-if="advertencias.otras && advertencias.otras.length > 0" show variant="info" class="mb-2">
            <ul class="mb-0">
              <li v-for="(advertencia, idx) in advertencias.otras" :key="'otra-modal-' + idx">
                <small>{{ advertencia }}</small>
              </li>
            </ul>
          </b-alert>
        </div>
        
        <div v-if="estadisticasRoles" class="mb-3">
          <strong>Resumen por roles:</strong>
          <ul class="mt-2">
            <li v-for="(cantidad, rol) in estadisticasRoles" :key="rol">
              <strong>{{ rol }}:</strong> {{ cantidad }} usuario(s)
            </li>
          </ul>
        </div>

        <p class="text-muted small mb-3">
          <b-icon icon="exclamation-triangle" class="mr-1"></b-icon>
          Esta acción no se puede deshacer.
        </p>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" @click="showConfirmModal = false">Cancelar</b-button>
          <b-button variant="success" @click="confirmarImportacion" class="ml-2" :disabled="importando">
            <b-spinner small v-if="importando" class="mr-1" />
            Sí, importar
          </b-button>
        </div>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import toast from "@/mixins/ToastMixin.js";

export default {
  name: 'ModalImportarUsuarios',
  mixins: [toast],
  components: { vSelect },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    gruposOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dominiosOptions: [],
      dominiosLoading: false,
      dominioSeleccionado: null,
      gruposSeleccionados: [],
      isGestorEscolar: false,
      dominioGestorBloqueado: false,
      archivoCsv: null,
      csvHasHeader: true,
      importando: false,
      cargandoPreview: false,
      previsualizacionDatos: null,
      totalRegistrosPreview: 0,
      estadisticasRoles: null,
      advertencias: null,
      showConfirmModal: false,
      columnMapping: {
        email: 'A',
        nombre: null,
        rol_plataforma: null
      },
      camposCsv: [
        { key: 'email', label: 'Email *', required: true },
        { key: 'nombre', label: 'Nombre (opcional)', required: false },
        { key: 'rol_plataforma', label: 'Rol Plataforma (opcional)', required: false }
      ],
      columnasAlfabeticas: [
        { value: 'A', text: 'A' }, { value: 'B', text: 'B' }, { value: 'C', text: 'C' },
        { value: 'D', text: 'D' }, { value: 'E', text: 'E' }, { value: 'F', text: 'F' },
        { value: 'G', text: 'G' }, { value: 'H', text: 'H' }, { value: 'I', text: 'I' },
        { value: 'J', text: 'J' }, { value: 'K', text: 'K' }, { value: 'L', text: 'L' },
        { value: 'M', text: 'M' }, { value: 'N', text: 'N' }, { value: 'O', text: 'O' },
        { value: 'P', text: 'P' }, { value: 'Q', text: 'Q' }, { value: 'R', text: 'R' },
        { value: 'S', text: 'S' }, { value: 'T', text: 'T' }, { value: 'U', text: 'U' },
        { value: 'V', text: 'V' }, { value: 'W', text: 'W' }, { value: 'X', text: 'X' },
        { value: 'Y', text: 'Y' }, { value: 'Z', text: 'Z' }
      ],
      camposPreview: [
        { key: 'index', label: '#' },
        { key: 'email', label: 'Email' },
        { key: 'nombre', label: 'Nombre' },
        { key: 'rol_plataforma', label: 'Rol Plataforma' }
      ]
    };
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.inicializar();
      }
    }
  },
  methods: {
    async inicializar() {
      this.resetForm();
      await this.verificarRolGestorEscolar();
      await this.cargarDominios();
    },
    resetForm() {
      this.dominioSeleccionado = null;
      this.gruposSeleccionados = [];
      this.archivoCsv = null;
      this.limpiarPreview();
      this.columnMapping = {
        email: 'A',
        nombre: null,
        rol_plataforma: null
      };
    },
    async verificarRolGestorEscolar() {
      const usuarioLogueado = this.$store.getters.usuario;
      this.isGestorEscolar = false;
      this.dominioGestorBloqueado = false;
      
      if (usuarioLogueado && usuarioLogueado.roles) {
        if (Array.isArray(usuarioLogueado.roles)) {
          this.isGestorEscolar = usuarioLogueado.roles.some(r => 
            r.slug === 'gestorEscolar' || r.name === 'gestorEscolar' || r.label === 'gestorEscolar'
          );
        } else if (typeof usuarioLogueado.roles === 'string') {
          this.isGestorEscolar = usuarioLogueado.roles.includes('gestorEscolar');
        }
      }
      
      if (!this.isGestorEscolar && usuarioLogueado && usuarioLogueado.roles) {
        this.isGestorEscolar = usuarioLogueado.roles.some(r => r.name === 'gestorEscolarEstudiante');
      }
    },
    async cargarDominios() {
      this.dominiosLoading = true;
      try {
        const response = await this.$api.get('/api/gestion-u/dominios-disponibles');
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          this.dominiosOptions = response.data.data.map(d => ({ 
            value: d.domain, 
            text: `${d.nombre} (${d.domain})`,
            domain: d.domain,
            nombre: d.nombre
          }));
          
          if (this.isGestorEscolar) {
            const usuarioLogueado = this.$store.getters.usuario;
            if (usuarioLogueado && usuarioLogueado.email) {
              const emailParts = usuarioLogueado.email.split('@');
              if (emailParts.length === 2) {
                const dominio = emailParts[1];
                const dominioEncontrado = this.dominiosOptions.find(d => d.value === dominio);
                if (dominioEncontrado) {
                  this.dominioSeleccionado = dominioEncontrado;
                  this.dominioGestorBloqueado = true;
                }
              }
            }
          }
        }
      } catch (e) {
        this.toast('b-toaster-bottom-center', true, 'No se pudieron cargar los dominios', 'danger');
      } finally {
        this.dominiosLoading = false;
      }
    },
    onArchivoChange() {
      this.limpiarPreview();
    },
    limpiarPreview() {
      this.previsualizacionDatos = null;
      this.totalRegistrosPreview = 0;
      this.estadisticasRoles = null;
      this.advertencias = null;
      this.cargandoPreview = false;
    },
    async previsualizarDatos() {
      if (!this.archivoCsv) {
        this.toast('b-toaster-bottom-center', true, 'Selecciona un archivo CSV', 'danger');
        return;
      }
      
      if (!this.columnMapping.email) {
        this.toast('b-toaster-bottom-center', true, 'Debes seleccionar la columna de Email', 'danger');
        return;
      }
      
      this.cargandoPreview = true;
      this.limpiarPreview();
      this.cargandoPreview = true;
      
      try {
        let formData = new FormData();
        formData.append('archivo', this.archivoCsv);
        
        if (this.dominioSeleccionado && this.dominioSeleccionado.value) {
          formData.append('dominio', this.dominioSeleccionado.value);
        }
        
        if (this.isGestorEscolar) {
          formData.append('forzar_rol_estudiante', '1');
        }
        
        const mappingFiltrado = Object.keys(this.columnMapping)
          .filter(key => this.columnMapping[key] !== null)
          .reduce((obj, key) => {
            obj[key] = this.columnMapping[key];
            return obj;
          }, {});
        
        formData.append('column_mapping', JSON.stringify(mappingFiltrado));
        formData.append('has_header', this.csvHasHeader);
        
        const response = await this.$api.post('/api/gestion-u/previsualizar-csv', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          this.previsualizacionDatos = response.data.data?.preview || response.data.preview || [];
          this.totalRegistrosPreview = response.data.data?.total || response.data.total || 0;
          this.estadisticasRoles = response.data.data?.estadisticas_roles || response.data.estadisticas_roles || null;
          this.advertencias = response.data.data?.advertencias || response.data.advertencias || null;
          
          if (this.previsualizacionDatos.length === 0) {
            this.toast('b-toaster-bottom-center', true, 'No se encontraron datos en el archivo CSV', 'warning');
          }
        } else {
          this.toast('b-toaster-bottom-center', true, response.data.message || 'Error al previsualizar', 'danger');
        }
      } catch (e) {
        this.toast('b-toaster-bottom-center', true, this.obtenerMensajeError(e), 'danger');
      } finally {
        this.cargandoPreview = false;
      }
    },
    mostrarConfirmacion() {
      this.showConfirmModal = true;
    },
    async confirmarImportacion() {
      this.showConfirmModal = false;
      await this.importarUsuarios();
    },
    async importarUsuarios() {
      if (!this.archivoCsv || !this.columnMapping.email) {
        return;
      }
      
      this.importando = true;
      try {
        let formData = new FormData();
        formData.append('archivo', this.archivoCsv);
        
        if (this.dominioSeleccionado && this.dominioSeleccionado.value) {
          formData.append('dominio', this.dominioSeleccionado.value);
        }
        
        if (this.gruposSeleccionados && this.gruposSeleccionados.length > 0) {
          formData.append('grupos', JSON.stringify(this.gruposSeleccionados));
        }
        
        if (this.isGestorEscolar) {
          formData.append('forzar_rol_estudiante', '1');
        }
        
        const mappingFiltrado = Object.keys(this.columnMapping)
          .filter(key => this.columnMapping[key] !== null)
          .reduce((obj, key) => {
            obj[key] = this.columnMapping[key];
            return obj;
          }, {});
        
        formData.append('column_mapping', JSON.stringify(mappingFiltrado));
        formData.append('has_header', this.csvHasHeader);
        
        if (this.advertencias && Object.keys(this.advertencias).length > 0) {
          formData.append('advertencias_preview', JSON.stringify(this.advertencias));
        }
        
        const response = await this.$api.post('/api/gestion-u/importar-masivo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        let color = response.data.color || 'success';
        let msg = response.data.message || 'Importación completada';
        
        this.toast('b-toaster-bottom-center', true, msg, color);
        this.cerrarModal();
        this.$emit('importacion-exitosa');
      } catch (e) {
        this.toast('b-toaster-bottom-center', true, this.obtenerMensajeError(e), 'danger');
      } finally {
        this.importando = false;
      }
    },
    obtenerMensajeError(e) {
      const errorMessage = e.message || '';
      
      if (errorMessage.includes('Network Error') && !e.response) {
        if (this.archivoCsv) {
          this.archivoCsv = null;
          return 'El archivo seleccionado fue modificado, movido o eliminado. Por favor, vuelve a seleccionarlo.';
        }
        return 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.';
      } else if (e.response && e.response.data && e.response.data.message) {
        return e.response.data.message;
      }
      
      return errorMessage || 'Error desconocido';
    },
    cerrarModal() {
      this.showModal = false;
    },
    onClose() {
      this.resetForm();
    }
  }
};
</script>
